import { Box, Button, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState, useRef } from 'react';
import { Controlled as CodeMirror } from 'react-codemirror2';
import ReactQuill from 'react-quill';
import _ from 'lodash'; // Import lodash for debounce

import 'codemirror/lib/codemirror.css';
import 'codemirror/mode/xml/xml';
import 'react-quill/dist/quill.snow.css';

const firstCleanHtmlContent = (html) => {
    // Find the index of the last closing </p> tag
    const lastClosingPIndex = html.lastIndexOf('</p>');
    if (lastClosingPIndex === -1) return html; // No closing </p> tag found
    // Find the corresponding opening <p> tag by searching backward from the last closing </p> tag
    const beforeLastClosingP = html.substring(0, lastClosingPIndex);
    const lastOpeningPIndex = beforeLastClosingP.lastIndexOf('<p');
    if (lastOpeningPIndex === -1) return html; // No opening <p> tag found
    // Construct the new HTML by removing the last <p>...</p> tag pair
    const partBeforeOpeningP = html.substring(0, lastOpeningPIndex);
    const contentBetweenP = html.substring(lastOpeningPIndex + beforeLastClosingP.substring(lastOpeningPIndex).indexOf('>') + 1, lastClosingPIndex);
    const partAfterClosingP = html.substring(lastClosingPIndex + 4);
    let cleaned = partBeforeOpeningP + contentBetweenP + partAfterClosingP;
    return cleaned.replace(/<p><br\s*\/?><\/p>/g, '<br>');
};

const cleanHtmlContent = (html) => {
    if (!html) return;
    
    let first = firstCleanHtmlContent(html)
    // Remove <p> tags and add an additional <br> where </p> tags were
    let cleaned = first.replace(/<\/p>/g, '<br>').replace(/<p[^>]*>/g, '');
    
    // Check if the cleaned content is empty or nearly empty
    if (!cleaned || cleaned === "<br>" || cleaned === "<h1><br></h1>" || cleaned === "<h2><br></h2>") {
        return '';
    }
    
    return cleaned;
};

// ``cleanHtmlContent`` unwraps <p> into <br>, which suits the notification
// templates this editor was built for (their renderers want <br>). Consumers
// that round-trip through a paragraph-aware format -- e.g. the knowledge base,
// where Quill HTML is converted to Markdown on save -- pass ``preserveParagraphs``
// so the block structure survives.
const normalizeHtml = (html, preserveParagraphs) =>
    preserveParagraphs ? (html || '') : (cleanHtmlContent(html) || '');

const QuillEditor = (props) => {
    const preserveParagraphs = props.preserveParagraphs;
    const [codemirrorContent, setCodemirrorContent] = useState(() => normalizeHtml(props.value, preserveParagraphs));
    const [quillContent, setQuillContent] = useState(() => normalizeHtml(props.value, preserveParagraphs));
    const [isEditingHTML, setIsEditingHTML] = useState(false);
    const quillRef = useRef(null);
    const codeMirrorRef = useRef(null);
    const onChangeRef = useRef(props.onChange);
    const skipNextPropsSyncRef = useRef(false);

    const [anchorEl, setAnchorEl] = useState(null);
    const [menuPath, setMenuPath] = useState([]); // array of keys representing current path in variables tree

    const containerHeight = props.containerHeight || '150px';

    const useEditorStyles = makeStyles(theme => ({
        quillEditor: {
            width: '100%',
            minHeight: '100px',
            // Just enough breathing room from the editor border to the
            // EDIT HTML / INSERT VARIABLE buttons below — the prior 2rem
            // left a visibly empty band between the two.
            paddingBottom: '0.25rem',
            '& .ql-container': {
                height: 'auto !important',
            },
            '& .ql-editor': {
                fontSize: '16px !important', // Ensure font size to prevent zoom in ios
                height: `${containerHeight} !important`,
                overflowY: 'scroll !important',
            },
        },
        CodeMirror: {
            width: '100%',
            maxWidth: '100%',
            whiteSpace: 'pre-wrap !important',
            wordWrap: 'break-word !important',
            overflowWrap: 'break-word !important',
            height: 'auto',
            fontSize: '16px !important',
        },
        buttonBar: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: theme.spacing(1),
            marginTop: '8px',
            '& .MuiButtonBase-root': {
                marginTop: '0 !important',
            },
        },
    }));    

    const classes = useEditorStyles();

    const handleQuillChange = (content, delta, source, editor) => {
        setQuillContent(content);
    };

    const handleCodeMirrorChange = (editor, data, value) => {
        setCodemirrorContent(value);
    };

    const toggleEditingHTML = () => {
        if (!isEditingHTML) {
            setCodemirrorContent(normalizeHtml(quillContent, preserveParagraphs));
        } else {
            setQuillContent(codemirrorContent);
        }
        setIsEditingHTML(!isEditingHTML);
    };

    const insertAtCursor = (text) => {
        if (!text) return;
        if (!isEditingHTML) {
            const editor = quillRef.current ? quillRef.current.getEditor() : null;
            if (editor) {
                const sel = editor.getSelection(true);
                const index = sel ? sel.index : editor.getLength();
                editor.insertText(index, text);
                editor.setSelection(index + text.length, 0);
                setQuillContent(editor.root.innerHTML);
            } else {
                setQuillContent((prev) => (prev || '') + text);
            }
            return;
        }
        const cm = codeMirrorRef.current;
        if (cm) {
            const doc = cm.getDoc();
            const cursor = doc.getCursor();
            doc.replaceRange(text, cursor);
            const newCursor = { line: cursor.line, ch: cursor.ch + text.length };
            doc.setCursor(newCursor);
            setCodemirrorContent(doc.getValue());
        } else {
            setCodemirrorContent((prev) => (prev || '') + text);
        }
    };

    const openVariablesMenu = (event) => {
        if (!hasVariables) return;
        setAnchorEl(event.currentTarget);
        setMenuPath([]);
    };
    const closeVariablesMenu = () => {
        setAnchorEl(null);
        setMenuPath([]);
    };

    const variablesTree = props.variablesTree || null;
    const hasVariables = React.useMemo(() => {
        if (!variablesTree || typeof variablesTree !== 'object') return false;
        return Object.keys(variablesTree).length > 0;
    }, [variablesTree]);
    const currentNode = React.useMemo(() => {
        if (!variablesTree) return null;
        let node = variablesTree;
        for (const key of menuPath) {
            if (node && typeof node === 'object') {
                node = node[key];
            }
        }
        return node;
    }, [variablesTree, menuPath]);

    const handleMenuItemClick = (key) => {
        if (!currentNode) return;
        const value = currentNode[key];
        if (value && typeof value === 'object') {
            setMenuPath((prev) => [...prev, key]);
        } else {
            // A string leaf is an explicit template path that overrides the
            // menu-key path (lets a label live under one group while
            // inserting a token from another scope).
            const path =
                typeof value === 'string'
                    ? value
                    : [...menuPath, key].join('.');
            insertAtCursor(`{{ ${path} }}`);
            closeVariablesMenu();
        }
    };

    const handleBack = () => {
        setMenuPath((prev) => prev.slice(0, -1));
    };

    const centerTooltip = () => {
        const tooltip = document.querySelector('.ql-tooltip');
        if (tooltip) {
            tooltip.style.left = '50%';
            tooltip.style.transform = 'translateX(-50%)';
        }
    };

    useEffect(() => {
        const quillEditor = document.querySelector('.ql-editor');
        if (quillEditor) {
            quillEditor.addEventListener('click', centerTooltip);
        }
        return () => {
            if (quillEditor) {
                quillEditor.removeEventListener('click', centerTooltip);
            }
        };
    }, []);

    onChangeRef.current = props.onChange;

    // ``onInsertImage`` lets the parent customize the toolbar image button
    // (e.g. open a dedicated Attachment picker dialog instead of Quill's
    // default URL prompt). Signature: ``onInsertImage(insertImageFn)``
    // where ``insertImageFn(url, alt?)`` inserts the image at the cursor
    // via Quill's API. When the prop is omitted we fall through to
    // Quill's built-in behavior (inline URL tooltip).
    const onInsertImageRef = useRef(props.onInsertImage);
    onInsertImageRef.current = props.onInsertImage;

    const insertImageAtCursor = (url, alt) => {
        if (!url) return;
        const editor = quillRef.current ? quillRef.current.getEditor() : null;
        if (!editor) return;
        const sel = editor.getSelection(true);
        const index = sel ? sel.index : editor.getLength();
        editor.insertEmbed(index, 'image', url, 'user');
        if (alt) {
            // After the embed, Quill puts the cursor right after the image —
            // walk back one to tag the embed with an alt attribute.
            try {
                const [leaf] = editor.getLeaf(index + 1);
                if (leaf && leaf.domNode && leaf.domNode.tagName === 'IMG') {
                    leaf.domNode.setAttribute('alt', alt);
                    setQuillContent(editor.root.innerHTML);
                }
            } catch (e) { /* alt tag is best-effort */ }
        }
        editor.setSelection(index + 1, 0);
    };

    // Memoize the Quill modules config so the toolbar handler closes over
    // the latest ``onInsertImage`` callback without re-instantiating the
    // editor on every render. Switching out modules mid-mount drops focus.
    // ``props.modules`` is read once, on mount, for the same reason the deps
    // array is empty: swapping modules on a live editor drops focus. Pass a
    // module-level constant, not an object literal.
    const quillModules = React.useMemo(() => {
        const source = props.modules || QuillEditor.modules;
        const base = {
            ...source,
            toolbar: {
                container: source.toolbar,
                handlers: {
                    image: function () {
                        const fn = onInsertImageRef.current;
                        if (typeof fn === 'function') {
                            // Save the current selection range before the
                            // parent's dialog steals focus, so the inserted
                            // image lands where the cursor was.
                            const editor = quillRef.current ? quillRef.current.getEditor() : null;
                            const sel = editor ? editor.getSelection() : null;
                            const restoreAndInsert = (url, alt) => {
                                if (editor && sel) editor.setSelection(sel.index, sel.length || 0);
                                insertImageAtCursor(url, alt);
                            };
                            fn(restoreAndInsert);
                            return;
                        }
                        // No custom handler — fall through to Quill's
                        // default (inline URL tooltip).
                        const editor = quillRef.current ? quillRef.current.getEditor() : null;
                        if (!editor) return;
                        const url = window.prompt('Image URL:');
                        if (url) insertImageAtCursor(url);
                    },
                    // Quill's built-in link handler silently no-ops when the
                    // user clicks the link button with no text selected —
                    // confusing for operators. Override to prompt for both
                    // URL and display text when the selection is empty, so
                    // the button always does something visible.
                    link: function (value) {
                        const editor = quillRef.current ? quillRef.current.getEditor() : null;
                        if (!editor) return;
                        const sel = editor.getSelection(true);
                        const hasSelection = sel && sel.length > 0;
                        if (value === false) {
                            // Toolbar toggled off on an active link — strip
                            // the format from the current selection.
                            if (hasSelection) editor.format('link', false, 'user');
                            return;
                        }
                        if (hasSelection) {
                            // Selection present — fall through to Quill's
                            // built-in URL tooltip so the selected text
                            // becomes the link label.
                            const tooltip = this.quill && this.quill.theme && this.quill.theme.tooltip;
                            if (tooltip) {
                                tooltip.edit('link', '');
                                return;
                            }
                            // Tooltip unavailable — prompt for URL only.
                            const url = window.prompt('Link URL:');
                            if (url) editor.format('link', url, 'user');
                            return;
                        }
                        // No selection — prompt for URL + display text and
                        // insert a fresh link at the cursor.
                        const url = window.prompt('Link URL:');
                        if (!url) return;
                        const text = window.prompt('Link text:', url) || url;
                        const index = sel ? sel.index : editor.getLength();
                        editor.insertText(index, text, 'link', url, 'user');
                        editor.setSelection(index + text.length, 0);
                        setQuillContent(editor.root.innerHTML);
                    },
                },
            },
        };
        return base;
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally mount-only; see comment above
    }, []);

    const debouncedOnChange = useRef(
        _.debounce((newContent) => {
            skipNextPropsSyncRef.current = true;
            onChangeRef.current(newContent);
        }, 300)
    ).current;

    useEffect(() => {
        const externalContent = normalizeHtml(props.value, preserveParagraphs);
        const internalContent = normalizeHtml(isEditingHTML ? codemirrorContent : quillContent, preserveParagraphs);

        if (skipNextPropsSyncRef.current) {
            skipNextPropsSyncRef.current = false;
            return;
        }

        if (externalContent !== internalContent) {
            setCodemirrorContent(externalContent);
            setQuillContent(externalContent);
        }
    }, [props.value]); // eslint-disable-line react-hooks/exhaustive-deps -- only react to external value; compare against latest editor state on that snapshot

    useEffect(() => {
        const newContent = normalizeHtml(isEditingHTML ? codemirrorContent : quillContent, preserveParagraphs);
        const externalContent = normalizeHtml(props.value, preserveParagraphs);

        if (newContent !== externalContent) {
            debouncedOnChange(newContent);
        }
    }, [codemirrorContent, quillContent, isEditingHTML, props.value, preserveParagraphs, debouncedOnChange]);

    useEffect(() => {
        return () => {
            debouncedOnChange.cancel();
        };
    }, [debouncedOnChange]);

    return (
        <div style={{ flexGrow: 1 }}>
            {!isEditingHTML && 
                <ReactQuill
                    ref={quillRef}
                    value={quillContent}
                    onChange={handleQuillChange}
                    modules={quillModules}
                    formats={props.formats || QuillEditor.formats}
                    className={classes.quillEditor}
                />}
            {isEditingHTML && (
                <CodeMirror
                    value={codemirrorContent}
                    className={classes.CodeMirror}
                    options={{
                        mode: 'xml',
                        theme: 'default',
                        lineNumbers: true,
                        lineWrapping: true,
                    }}
                    editorDidMount={(editor) => {
                        codeMirrorRef.current = editor;
                    }}
                    onBeforeChange={(editor, data, value) => {
                        if (!codeMirrorRef.current) {
                            codeMirrorRef.current = editor;
                        }
                        setCodemirrorContent(value);
                    }}
                    onChange={handleCodeMirrorChange}
                />
            )}
            {/* Consumers that supply their own controls (e.g. the knowledge
                base dialog, which toggles Markdown source rather than HTML)
                hide this bar entirely. */}
            {!props.hideButtonBar && (
            <Box className={classes.buttonBar}>
                <Button
                    variant="outlined"
                    onClick={toggleEditingHTML}
                >
                    {isEditingHTML ? 'Hide HTML' : 'Edit HTML'}
                </Button>
                {!isEditingHTML && (
                    <Tooltip title={hasVariables ? '' : 'Variables are not available'}>
                        <span>
                            <Button
                                variant="outlined"
                                onClick={openVariablesMenu}
                                disabled={!hasVariables}
                            >
                                Insert variable
                            </Button>
                        </span>
                    </Tooltip>
                )}
                {/* Slot for consumer-supplied buttons (e.g. "Preview email")
                    so they share the same outlined-button row instead of
                    floating below in their own Box with mismatched spacing. */}
                {props.extraButtons}
            </Box>
            )}

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeVariablesMenu}>
                {menuPath.length > 0 && (
                    <MenuItem onClick={handleBack}>
                        <Typography>{'<'}</Typography>
                        <Typography sx={{ ml: 1 }}>Back</Typography>
                    </MenuItem>
                )}
                {currentNode && typeof currentNode === 'object' && Object.keys(currentNode).sort().map((key) => {
                    const value = currentNode[key];
                    const isLeaf = !(value && typeof value === 'object');
                    return (
                        <MenuItem key={key} onClick={() => handleMenuItemClick(key)}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                <Typography>{key}</Typography>
                                {!isLeaf && <ChevronRightIcon fontSize="small" />}
                            </Box>
                        </MenuItem>
                    );
                })}
            </Menu>
        </div>
    );
};

QuillEditor.modules = {
    toolbar: [
        [{ 'header': '1' }, { 'header': '2' }],
        ['bold', 'italic', 'underline'],
        [{ 'list': 'bullet' }],
        ['link', 'image'],
        ['clean']
    ],
    clipboard: {
        matchVisual: false,
        allowed: {
            // ``img`` + ``src``/``alt``/``width``/``height`` are required so
            // inline images survive a paste / round-trip through the
            // editor. Without them the clipboard module strips the tag and
            // the operator's inserted image disappears on the next render.
            tags: ['div', 'a', 'b', 'strong', 'u', 'i', 'br', 'ul', 'ol', 'li', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'img'],
            attributes: ['href', 'rel', 'target', 'class', 'style', 'src', 'alt', 'width', 'height']
        }
    },
};

QuillEditor.formats = [
    'header',
    'bold', 'italic', 'underline',
    'list', 'bullet', 'indent',
    'link', 'image',
];

export default QuillEditor;
